import { useEventLogging } from '@AppModels/Logging';
import { IRegEdSubmissionMetadata, RegEdComplianceContentType, submitRegEdCompliance } from '@Queries/RegEdCompliance';
import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import {
	Redirect,
	RouteComponentProps,
	Switch,
	useHistory,
	useLocation,
	useParams,
	useRouteMatch,
} from 'react-router-dom';
import { toast } from 'sonner';
import {
	ICampaignsCreatedNotificationInfo,
	ICreateCampaignRequest,
	ILocationState,
	ImpersonationContextKey,
	TemplateOrTemplateFilter,
} from '../../../../models';
import { CampaignType } from '../../../../models/AdminModels';
import { EmailComposerContext, IEmailComposerContext } from '../../../../models/Email';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { postNotification } from '../../../../models/LocalNotifications';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { invalidateAllInfiniteCampaignReportQueries } from '../../../../queries';
import { useTemplate } from '../../../../queries/Templates/useTemplate';
import { ComposeEmailViewModel, TagViewModel } from '../../../../viewmodels/AppViewModels';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../components/ConfirmationDialog';
import { ComplianceApprovalPrompt } from '../../../components/campaigns/ComplianceApprovalPrompt';
import { ComplianceConfirmation } from '../../../components/campaigns/ComplianceConfirmation';
import { SendSchedulerCompleteOverlay } from '../../../components/email/SendSchedulerCompleteOverlay';
import { ScheduledEmailErrorModal } from '../../../components/errorMessages/ScheduledEmailErrorModal';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { MultiContainerHeader } from '../../../components/MultiContainerHeader';
import { PrivateRoute } from '../../../components/PrivateRoute';
import { WarningIcon } from '../../../components/svgs/icons/WarningIcon';
import { CampaignSessionManager, SessionProgressUpdate } from '../../shared/AIScheduleSuccess/campaignSessionManager';
import { EditCampaign, IEditCampaignComponent } from '../EditCampaign';
import { EditSendOptions } from '../EditSendOptions';

const MinSendSystemJobRecordCountForOverlay = 5;

const styleSheet = StyleSheet.create({
	childRoute: {
		boxSizing: 'border-box',
		height: '100%',
		overflow: 'auto',
	},
	container: {
		height: '100%',
		width: '100%',
	},
	loaderContainer: {
		alignItems: 'center',
		display: 'flex',
		height: '100%',
		justifyContent: 'center',
	},
});

interface IProps {
	impersonationContext?: Api.IImpersonationContext;
}

const _CampaignFromTemplatePage: React.FC<IProps> = ({ impersonationContext }) => {
	const userSession = useUserSession();
	const fullscreenModal = useFullscreenModal();
	const errorMessages = useErrorMessages();
	const { logApiError, logEvent, logInput } = useEventLogging('CampaignFromTemplatePage');
	const history = useHistory();
	const location = useLocation<ILocationState<any, ICreateCampaignRequest<TemplateOrTemplateFilter>>>();
	const match = useRouteMatch();
	const { templateId } = useParams<{ templateId: string }>();

	const editCampaignRef = React.useRef<IEditCampaignComponent | undefined>(undefined);
	const mCampaignCreateRequestRef = React.useRef<ICreateCampaignRequest | undefined>(location.state?.model);
	// RegEd submission (format + answers) captured on the compliance prompt; consumed after send()
	// once the campaign (and its server-built approval request) exists.
	const regEdMetadataRef = React.useRef<IRegEdSubmissionMetadata | null>(null);
	// In-progress form draft — survives the back-navigation to the editor (the compliance-options
	// sub-route unmounts the prompt while this page stays mounted).
	const regEdDraftRef = React.useRef<IRegEdSubmissionMetadata | null>(null);

	const [showingCloseConfirmation, setShowingCloseConfirmation] = React.useState(false);
	const [scheduleError, setScheduleError] = React.useState<Api.IOperationResultNoValue | null>(null);

	const [createByContentCalendar] = React.useState<boolean>(() => location.state?.referrer === 'contentCalendar');
	const [initialStartMoment] = React.useState<moment.Moment | undefined>(() => {
		const startDate = location.state?.model?.schedule?.startDate;
		return startDate ? moment(startDate).set('hours', 11) : undefined;
	});

	// canSendOnBehalf gates whether the composer is configured for cross-user sending. Mirrors BAS's
	// admin-and-account-pref check from the from-template case.
	const canSendOnBehalf =
		!!userSession?.user?.role?.toLocaleLowerCase()?.includes('admin') &&
		!!userSession?.account?.preferences?.sendOnBehalfEnabled;

	const {
		data: template,
		isLoading,
		error: templateError,
	} = useTemplate<Api.ITemplate>({
		templateId,
		expand: 'LastUsedByDate',
		impersonationContext,
		meta: {
			toastErrorMessage: 'Problem occurred loading template',
		},
	});

	const [emailComposer, setEmailComposer] =
		React.useState<ComposeEmailViewModel<Api.IEmailMessageFollowUpOptions> | null>(null);

	// Single effect: build composer + async tag-load → commit composer at the end so consumers never see
	// a partial state. Tag-load failure logs and proceeds with exclusion-only filter rather than stranding
	// the loading spinner.
	React.useEffect(() => {
		if (!template || emailComposer) {
			return;
		}
		let cancelled = false;

		(async () => {
			const composer = new ComposeEmailViewModel<Api.IEmailMessageFollowUpOptions>(userSession);
			composer.emailMessage = new Api.EmailMessageViewModel<
				File,
				Api.ISendEmailResponse,
				Api.IEmailMessageFollowUpOptions
			>(userSession);
			composer.allowSenderSelection = canSendOnBehalf;

			composer.emailMessage.setSavedAttachments(template.attachments);
			composer.emailMessage.templateReference = {
				isCustomized: false,
				isSystemTemplate: template.scope === Api.TemplateScope.Industry,
				templateId: template.id,
				name: template.name,
			};
			composer.emailMessage.subject = template.subject;
			composer.emailMessage.content = { ...template.content };
			if (template.templateType === Api.TemplateType.HtmlNewsletter) {
				composer.canCustomizeIndividualEmails = false;
			}
			if (
				(template.scope === Api.TemplateScope.Industry || template.scope === Api.TemplateScope.System) &&
				template.schedule?.expirationDate
			) {
				composer.emailMessage.options.scheduledSend = {
					...(composer.emailMessage.options.scheduledSend || {}),
					expirationDate: template.schedule.expirationDate,
				};
			}

			const exclusionCriteria: Api.IContactFilterCriteria[] = [
				{
					op: Api.FilterOperator.Not,
					property: Api.ContactFilterCriteriaProperty.WithoutEmailAddresses,
				},
				...Array.from(Api.DefaultBulkSendExcludedTags).map(
					x =>
						({
							op: Api.FilterOperator.Not,
							property: Api.ContactFilterCriteriaProperty.Tag,
							value: x,
						}) as Api.IContactFilterCriteria
				),
			];

			composer.emailMessage.contactsFilterRequest = {
				contactFilterRequest: { criteria: exclusionCriteria },
			};
			composer.emailMessage.setSendEmailFromUser(userSession.user);

			// Async tag-load. Wrapped in try/catch so a failure doesn't strand the loading spinner; on
			// failure we proceed with the exclusion-only filter the composer already has.
			const queryTags = location.state?.model?.tags;
			const tagsToCheck = typeof queryTags === 'string' ? [queryTags] : Array.isArray(queryTags) ? queryTags : [];
			if (tagsToCheck.length > 0) {
				try {
					const result = await Promise.all(
						tagsToCheck.map(x => {
							const tagVM = new TagViewModel(userSession, { name: x });
							return tagVM.load();
						})
					);
					if (cancelled) {
						return;
					}
					const foundTags = result
						?.filter(x => !!x)
						?.map((x: any) => {
							if ('tag' in x) {
								return x.tag;
							}
							return undefined;
						})
						?.filter(x => !!x);

					if (foundTags.length > 0) {
						// Shape matches BAS's configureEmailComposer output for from-template: a single OR-tag node
						// (no extra wrapper, no top-level And) followed by exclusion criteria. The tag-pill renderer
						// only walks one level deep, so any extra nesting hides the tag from the sidebar even though
						// the API filter still applies.
						const orTagCriteria: Api.IContactFilterCriteria = {
							criteria: foundTags.map(
								x =>
									({
										op: Api.FilterOperator.Or,
										property: Api.ContactFilterCriteriaProperty.Tag,
										value: x,
									}) as Api.IContactFilterCriteria
							),
							op: Api.FilterOperator.Or,
						};

						const combinedFilter: Api.IContactFilterCriteria = {
							criteria: [orTagCriteria, ...exclusionCriteria],
						};

						const ownershipFilter = !userSession.account.preferences?.showAllContactsByDefault
							? {
									criteria: {
										property: Api.ContactFilterCriteriaProperty.OwnedBy,
										value: userSession.user.id,
									},
								}
							: null;

						composer.emailMessage.contactsFilterRequest = {
							contactFilterRequest: combinedFilter,
							...(ownershipFilter ? { ownershipFilter } : {}),
						} as Api.IEmailMessageContactsFilterRequest;

						// Push BEFORE setEmailComposer so the URL is at /edit/recipients by the time anything
						// renders with the composer.
						history.replace(`${match.url}/edit/recipients`);
					}
				} catch (err) {
					logApiError('TagLoad-Error', err as Api.IOperationResultNoValue);
				}
			}

			if (cancelled) {
				return;
			}
			setEmailComposer(composer);
		})();

		return () => {
			cancelled = true;
		};
	}, [template, emailComposer, userSession, canSendOnBehalf, location, history, match.url, logApiError]);

	// Error path: template fetch failed. Toast + dismiss the modal.
	React.useEffect(() => {
		if (templateError && !template) {
			fullscreenModal?.dismissModal();
		}
	}, [templateError, template, fullscreenModal]);

	const onSendTestEmailToUser = React.useCallback(
		async (selectedUserId: string): Promise<Api.IOperationResult<Api.ISendEmailResponse>> => {
			editCampaignRef.current?.finishEditingContent?.();
			logEvent('SendEmailTest', { selectedUserId });
			if (!emailComposer?.emailMessage?.sendTestEmail) {
				throw new Error('sendTestEmail method not available');
			}
			const testEmailOptions: Api.ISendTestEmailOptions<Api.ContactViewModel> = {
				to: { id: selectedUserId } as Api.ContactViewModel,
			};
			return emailComposer.emailMessage.sendTestEmail(testEmailOptions);
		},
		[emailComposer, logEvent]
	);

	const composerContext = React.useMemo<IEmailComposerContext>(
		() => ({ emailComposer: emailComposer ?? undefined, sendTestEmail: onSendTestEmailToUser }),
		[emailComposer, onSendTestEmailToUser]
	);

	const dismissModal = React.useCallback(() => {
		fullscreenModal?.dismissModal();
	}, [fullscreenModal]);

	const onBackButtonClicked = React.useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			const pathComps = new Set((location.pathname || '').split('/'));
			if (pathComps.has('edit') && pathComps.has('send-complete')) {
				e.preventDefault();
				dismissModal();
			}
			editCampaignRef.current?.finishEditingContent?.();
		},
		[location.pathname, dismissModal]
	);

	const onCloseButtonClicked = React.useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			logInput('Close', 'Click');
			const pathComps = new Set((location.pathname || '').split('/'));
			if (pathComps.has('edit') && !pathComps.has('send-complete')) {
				e.preventDefault();
				setShowingCloseConfirmation(true);
			}
		},
		[location.pathname, logInput]
	);

	const onCloseConfirmationDialogRequestClose = React.useCallback(
		(result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
			setShowingCloseConfirmation(false);
			if (result?.isDestructive && !canceled) {
				dismissModal();
			}
		},
		[dismissModal]
	);

	const onCloseScheduleErrorModal = React.useCallback(() => {
		setScheduleError(null);
	}, []);

	const showScheduleComplete = React.useCallback(
		(schedule: Api.IScheduledSend) => {
			if (!emailComposer) {
				return;
			}
			emailComposer.sendEstimate = {
				...(emailComposer.sendEstimate || {}),
				estimate: schedule,
			};
			if (emailComposer?.emailMessage?.groupId) {
				invalidateAllInfiniteCampaignReportQueries();
			}

			const hasCampaignSession = CampaignSessionManager.hasActiveSession();
			const emailContext = CampaignSessionManager.createAIContextFromEmailData({
				emailMessage: emailComposer.emailMessage,
			});

			let sessionProgress: SessionProgressUpdate | null = null;
			const scheduledDate = schedule.startDate ? new Date(schedule.startDate) : undefined;
			const isSendNow = schedule.criteria === Api.ScheduleCriteria.Immediately;

			if (emailContext) {
				if (hasCampaignSession) {
					sessionProgress = CampaignSessionManager.updateSessionProgress('email', emailContext, scheduledDate);
				} else {
					CampaignSessionManager.startSession('email', emailContext, scheduledDate);
				}
			}

			if (CampaignSessionManager.isPrimaryResourceSendNow('email', isSendNow) && !hasCampaignSession) {
				CampaignSessionManager.clearSession();
				dismissModal();
				return;
			}

			if (CampaignSessionManager.hasActiveSession()) {
				if (sessionProgress?.isCampaignComplete) {
					CampaignSessionManager.clearSession();
					dismissModal();
					return;
				}
				const sessionId = sessionProgress?.session?.id ?? CampaignSessionManager.getSession()?.id;
				history.push({
					pathname: `${match.url}/edit/send-complete`,
					state: { sessionId },
				});
				return;
			}

			history.push(
				`${match.url}/${
					emailComposer.emailMessage.options?.sendWithCompliance ? 'edit/compliance-complete' : 'edit/send-complete'
				}`
			);
		},
		[emailComposer, history, match.url, dismissModal]
	);

	const send = React.useCallback(async () => {
		if (!emailComposer) {
			return;
		}
		try {
			const op = await emailComposer.emailMessage.send();
			const campaign = new Api.CampaignViewModel(userSession, { id: op.value.id });
			try {
				logEvent('LoadCampaign', { id: op.value.id });
				await campaign.load();
			} catch (err) {
				logApiError('LoadCampaign-Error', err as Api.IOperationResultNoValue);
			}

			// Isolated: the campaign is already sent, so a submission failure must not surface it as a
			// send error (which would drop the success path and invite a retry → duplicate approval).
			await submitRegEdCompliance({
				userSession,
				parentType: RegEdComplianceContentType.Email,
				parentId: op.value.id,
				approvalRequestId: campaign.toJs().approvalRequests?.[0]?.id,
				metadata: regEdMetadataRef.current,
				impersonationContext,
				logApiError,
				action: 'sent',
			});
			const info: ICampaignsCreatedNotificationInfo = {
				accountId: (impersonationContext?.account || userSession.account).id,
				campaigns: [campaign.toJs()],
			};
			postNotification({
				info,
				topic: Topics.CREATE_CAMPAIGNS_ITEM,
			});

			if (op.value.schedule.criteria === Api.ScheduleCriteria.Immediately) {
				const hasCampaignSession = CampaignSessionManager.hasActiveSession();
				const emailContext = CampaignSessionManager.createAIContextFromEmailData({
					emailMessage: emailComposer.emailMessage,
				});

				if (emailContext && hasCampaignSession) {
					CampaignSessionManager.updateSession('email', emailContext);
				}

				const isPrimarySendNow = CampaignSessionManager.isPrimaryResourceSendNow('email', true);

				if (isPrimarySendNow || !hasCampaignSession) {
					CampaignSessionManager.clearSession();
					if (campaign.totalCount < MinSendSystemJobRecordCountForOverlay) {
						postNotification({
							info: emailComposer.emailMessage,
							topic: Topics.SEND_EMAIL,
						});
						toast.success('Message sent successfully');
						dismissModal();
					} else {
						toast.success('Success. Your email is ready to send.');
						history.push(`/reporting/group-email/${op.value.id}`);
					}
				} else {
					showScheduleComplete(op.value.schedule);
				}
			} else {
				showScheduleComplete(op.value.schedule);
			}
			mCampaignCreateRequestRef.current?.onFinish?.(true);
		} catch (sendError: any) {
			logApiError('SendBulk-Error', sendError);
			const sendNow = emailComposer.emailMessage.options?.scheduledSend?.criteria === Api.ScheduleCriteria.Immediately;
			if (!sendNow && sendError?.systemCode === -1) {
				setScheduleError(sendError);
				return;
			}
			errorMessages.pushApiError(sendError);
		}
	}, [
		emailComposer,
		userSession,
		impersonationContext,
		logEvent,
		logApiError,
		errorMessages,
		history,
		dismissModal,
		showScheduleComplete,
	]);

	const onSendWithOutCompliance = React.useCallback(() => {
		if (!emailComposer) {
			return;
		}
		emailComposer.emailMessage.options = emailComposer.emailMessage.options || {};
		emailComposer.emailMessage.options.sendWithCompliance = false;
		history.push(`${match.url}/edit/send-options`);
	}, [emailComposer, history, match.url]);

	const onSendWithCompliance = React.useCallback(
		(sendWithComplianceEmail: string) => {
			if (!emailComposer) {
				return;
			}
			emailComposer.emailMessage.options = emailComposer.emailMessage.options || {};
			emailComposer.emailMessage.options.sendWithCompliance = true;
			emailComposer.emailMessage.options.sendWithComplianceEmail = sendWithComplianceEmail;
			history.push(`${match.url}/edit/send-options`);
		},
		[emailComposer, history, match.url]
	);

	const onSendWithRegEdCompliance = React.useCallback(
		(metadata: IRegEdSubmissionMetadata) => {
			if (!emailComposer) {
				return;
			}
			regEdMetadataRef.current = metadata;
			emailComposer.emailMessage.options = emailComposer.emailMessage.options || {};
			emailComposer.emailMessage.options.sendWithCompliance = true;
			history.push(`${match.url}/edit/send-options`);
		},
		[emailComposer, history, match.url]
	);

	const isFromHtmlNewsletterTemplate =
		template?.content?.sourceFormat === Api.ContentSourceFormat.UnlayerHtmlNewsletter;

	const renderFullscreenHeader = () => {
		if (emailComposer?.isUserEditingMessage) {
			return null;
		}
		let title = '';
		if (location.pathname === `${match.url}/edit/send-options`) {
			title = 'Send Options';
		} else if (location.pathname === `${match.url}/edit/compliance-options`) {
			title = 'Compliance Approval';
		} else if (location.pathname === `${match.url}/edit/compliance-complete`) {
			title = 'Confirmation';
		} else if (new RegExp(`^${match.url}/edit`, 'i').test(location.pathname)) {
			title = 'View Campaign';
		}
		if (isFromHtmlNewsletterTemplate) {
			title = 'Send HTML Email';
		}
		if (!title) {
			return null;
		}
		return (
			<MultiContainerHeader
				fullscreenHeader={title}
				onFullscreenRequestBack={onBackButtonClicked}
				onFullscreenRequestClose={onCloseButtonClicked}
			/>
		);
	};

	if (isLoading || !emailComposer) {
		return (
			<div className={`${css(styleSheet.container)} campaign-from-template-page`}>
				<MultiContainerHeader
					fullscreenHeader={isFromHtmlNewsletterTemplate ? 'Send HTML Email' : 'View Campaign'}
					onFullscreenRequestBack={onBackButtonClicked}
					onFullscreenRequestClose={onCloseButtonClicked}
				/>
				<div className={css(styleSheet.loaderContainer)}>
					<LoadingSpinner type='large' />
				</div>
			</div>
		);
	}

	const onRenderEditCampaign = (props: RouteComponentProps<any>) => (
		<EditCampaign
			{...props}
			canImpersonateOnTemplateSave={createByContentCalendar}
			emailComposer={emailComposer}
			initialEmailBodyTemplate={template}
			onInnerRef={(ref?: IEditCampaignComponent) => {
				editCampaignRef.current = ref;
			}}
			styles={[styleSheet.childRoute]}
		/>
	);

	const onRenderEditSendOptions = (props: RouteComponentProps<any>) => (
		<EditSendOptions
			{...props}
			disableSendNow={!emailComposer.emailMessage.isSendFromOption(Api.SendEmailFrom.CurrentUser)}
			emailComposer={emailComposer}
			initialStartDate={initialStartMoment}
			onScheduleCtaClicked={send}
			styles={[styleSheet.childRoute]}
		/>
	);

	const onRenderComplianceOptions = (props: RouteComponentProps<any>) => (
		<ComplianceApprovalPrompt
			{...props}
			campaignType={isFromHtmlNewsletterTemplate ? CampaignType.htmlNewsletter : CampaignType.Email}
			regEdInitialFormat={regEdDraftRef.current?.format}
			regEdInitialAnswers={regEdDraftRef.current?.answers}
			onRegEdFormStateChange={metadata => {
				regEdDraftRef.current = metadata;
			}}
			onScheduleCompliance={(e: React.MouseEvent<HTMLElement>, sendWithComplianceEmail: string) => {
				e.preventDefault();
				onSendWithCompliance(sendWithComplianceEmail);
			}}
			onScheduleRegEdCompliance={(e, metadata) => {
				e.preventDefault();
				onSendWithRegEdCompliance(metadata);
			}}
			onScheduleWithoutCompliance={onSendWithOutCompliance}
		/>
	);

	const onRenderSendComplete = (props: RouteComponentProps<any>) => (
		<SendSchedulerCompleteOverlay
			{...props}
			emailComposer={emailComposer}
			emailCount={emailComposer.estimatedRecipientsTotal}
			estimate={emailComposer.sendEstimate}
			onCloseButtonClicked={dismissModal}
			styles={[styleSheet.childRoute]}
		/>
	);

	return (
		<div className={`${css(styleSheet.container)} campaign-from-template-page`}>
			<EmailComposerContext.Provider value={composerContext}>
				{renderFullscreenHeader()}
				<Switch>
					<Redirect exact={true} from={match.url} to={`${match.url}/edit/tags`} />
					<PrivateRoute
						exact={true}
						location={location}
						path={`${match.url}/edit/send-options`}
						render={onRenderEditSendOptions}
						userSession={userSession}
					/>
					<PrivateRoute
						exact={true}
						location={location}
						path={`${match.url}/edit/compliance-options`}
						render={onRenderComplianceOptions}
						userSession={userSession}
					/>
					<PrivateRoute
						exact={true}
						location={location}
						path={`${match.url}/edit/send-complete`}
						render={onRenderSendComplete}
						userSession={userSession}
					/>
					<PrivateRoute
						exact={true}
						location={location}
						path={`${match.url}/edit/compliance-complete`}
						userSession={userSession}
					>
						<ComplianceConfirmation onDismiss={dismissModal} styles={[styleSheet.childRoute]} />
					</PrivateRoute>
					<PrivateRoute
						location={location}
						path={`${match.url}/edit`}
						render={onRenderEditCampaign}
						userSession={userSession}
					/>
				</Switch>
				<ConfirmationDialog
					icon={<WarningIcon />}
					modalProps={{
						isOpen: showingCloseConfirmation,
						onRequestClose: onCloseConfirmationDialogRequestClose,
					}}
					options={[
						{ isDestructive: true, representedObject: true, title: 'Discard' },
						{ isCancel: true, representedObject: false, title: 'Cancel' },
					]}
					title='Are you sure you want to discard this email?'
				/>
				<ScheduledEmailErrorModal
					onNavigateToReportingGroupEmail={dismissModal}
					onRequestClose={onCloseScheduleErrorModal}
					scheduleError={scheduleError}
				/>
			</EmailComposerContext.Provider>
		</div>
	);
};

export const CampaignFromTemplatePage = inject(ImpersonationContextKey)(observer(_CampaignFromTemplatePage));
